// ════════════════════════════════════════════════════════════════════
// 📖 RulezRender.js — THE IN-APP BOOK *IS* RULEZ_V2.md
// ════════════════════════════════════════════════════════════════════
// caP'n, 2026-09-13, asked whether the in-app Rulebook should be
// rewritten by hand to match `RULEZ_V2.md` or should simply RENDER it:
//
//   **"16) hell yeah beautiful :3"**
//
// ⚠ THIS EXISTS BECAUSE A HAND-KEPT COPY DRIFTS, AND THIS ONE HAD.
// The coherency pass found twenty-six places where the in-app Rulebook
// and the book disagreed — a Curio that took "One Ally Ilk" where the
// book says two, an U'hlti attachable "from L1" where the book says L3,
// a Turn defined as half a Turn, and two whole chapters the book has and
// the app did not. Every one of those was somebody faithfully copying a
// rule and the rule moving afterwards. **The only copy that cannot drift
// is the one that is not a copy.**
//
// ── WHAT THIS IS NOT ────────────────────────────────────────────────
// It is not a general Markdown library and must not become one. It
// renders exactly the subset `RULEZ_V2.md` uses — headings, paragraphs,
// bold/italic/code, bullet and numbered lists, tables, blockquotes,
// horizontal rules — in the Grimoire's own type. Anything the book does
// not use is deliberately absent, because an unused branch is a branch
// nobody has ever seen render.
//
// ⚠ AND IT NEVER INTERPRETS. No rule is computed here, no number is
// derived, nothing is reordered or summarised. If a sentence is wrong on
// screen it is wrong in `RULEZ_V2.md`, which is the entire point: there
// is now exactly one place to fix it.
// ════════════════════════════════════════════════════════════════════
import { RULEZ_V2_MD } from "./rulezV2";
import { GM } from "./theme";

const JX = GM.amethystBright;

// ── INLINE: **bold**, *italic*, `code` ──────────────────────────────
// One pass, left to right, because the book nests at most one level and
// a recursive-descent parser for prose nobody nests is a liability.
const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
function inline(text, keyBase) {
  const out = [];
  let i = 0;
  String(text).split(INLINE).forEach((piece, n) => {
    if (!piece) return;
    const k = `${keyBase}-${n}`;
    if (piece.startsWith("**") && piece.endsWith("**")) {
      out.push(<strong key={k} style={{ color: GM.inkGoldBright, fontWeight: 900 }}>{piece.slice(2, -2)}</strong>);
    } else if (piece.startsWith("*") && piece.endsWith("*") && piece.length > 2) {
      out.push(<em key={k} style={{ color: GM.vellum }}>{piece.slice(1, -1)}</em>);
    } else if (piece.startsWith("`") && piece.endsWith("`")) {
      out.push(<code key={k} style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.92em", color: JX, background: `${JX}14`, padding: "1px 5px", borderRadius: 3 }}>{piece.slice(1, -1)}</code>);
    } else {
      out.push(<span key={k}>{piece}</span>);
      i += 1;
    }
  });
  return out;
}

const cells = (row) => row.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
const isDivider = (row) => /^\|?[\s:|-]+\|[\s:|-]*$/.test(row) && row.includes("-");

/**
 * Render one section of the book. `md` is raw Markdown — a chapter's
 * body, or the whole thing.
 */
export function RulezMarkdown({ md, style }) {
  const lines = String(md || "").split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;
  const push = (el) => blocks.push(el);

  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();

    // blank
    if (!t) { i++; continue; }

    // horizontal rule / the book's own ◆ ◆ ◆ divider
    if (/^---+$/.test(t) || /^◆( ◆)+$/.test(t)) {
      push(<div key={`hr${key++}`} style={{ textAlign: "center", color: GM.inkGoldDim, letterSpacing: 8, margin: "26px 0", fontSize: 13 }}>◆ ◆ ◆</div>);
      i++; continue;
    }

    // headings
    const h = /^(#{1,6})\s+(.*)$/.exec(t);
    if (h) {
      const depth = h[1].length;
      const size = depth === 1 ? 26 : depth === 2 ? 22 : depth === 3 ? 17 : 15;
      push(
        <div key={`h${key++}`} style={{
          fontFamily: GM.font, fontWeight: 900, fontStyle: "italic",
          color: depth <= 2 ? GM.inkGoldBright : GM.vellum,
          fontSize: size, letterSpacing: depth <= 2 ? 1 : 0,
          margin: depth <= 2 ? "30px 0 12px" : "22px 0 8px",
          borderBottom: depth <= 2 ? `1px solid ${GM.inkGold}44` : "none",
          paddingBottom: depth <= 2 ? 6 : 0,
        }}>{inline(h[2], `h${key}`)}</div>
      );
      i++; continue;
    }

    // blockquote — the book uses these for J'ynxi's asides
    if (t.startsWith(">")) {
      const buf = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        buf.push(lines[i].trim().replace(/^>\s?/, "")); i++;
      }
      push(
        <div key={`q${key++}`} style={{
          borderLeft: `3px solid ${JX}88`, background: `${JX}0c`,
          padding: "10px 16px", margin: "14px 0", borderRadius: "0 3px 3px 0",
          fontStyle: "italic", color: GM.vellumDim, lineHeight: 1.75,
        }}>{inline(buf.join(" "), `q${key}`)}</div>
      );
      continue;
    }

    // table
    if (t.startsWith("|") && i + 1 < lines.length && isDivider(lines[i + 1].trim())) {
      const head = cells(t);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { rows.push(cells(lines[i].trim())); i++; }
      // ⚠ THE BOOK USES HEADERLESS TABLES — `| | |` — as two-column
      // definition blocks, and printing an empty header row on those
      // would put a gold rule above nothing.
      const headed = head.some((c) => c.length > 0);
      push(
        <div key={`t${key++}`} style={{ overflowX: "auto", margin: "16px 0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            {headed && (
              <thead>
                <tr>{head.map((c, n) => (
                  <th key={n} style={{ textAlign: "left", padding: "8px 10px", color: GM.inkGoldBright, fontFamily: GM.font, fontWeight: 900, fontSize: 12, letterSpacing: 1, borderBottom: `1px solid ${GM.inkGold}66`, whiteSpace: "nowrap" }}>{inline(c, `th${n}`)}</th>
                ))}</tr>
              </thead>
            )}
            <tbody>
              {rows.map((r, rn) => (
                <tr key={rn} style={{ background: rn % 2 ? `${GM.inkGold}07` : "transparent" }}>
                  {r.map((c, cn) => (
                    <td key={cn} style={{ padding: "8px 10px", color: GM.vellumDim, verticalAlign: "top", borderBottom: `1px solid ${GM.pageBorder}`, lineHeight: 1.6 }}>{inline(c, `td${rn}-${cn}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // lists — bullet and numbered, kept as ONE block so the spacing is
    // a list's spacing rather than a stack of paragraphs
    const bullet = /^[-*]\s+(.*)$/.exec(t);
    const numbered = /^(\d+)\.\s+(.*)$/.exec(t);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const items = [];
      while (i < lines.length) {
        const li = lines[i].trim();
        const b = /^[-*]\s+(.*)$/.exec(li);
        const n = /^(\d+)\.\s+(.*)$/.exec(li);
        if (ordered && n) items.push(n[2]);
        else if (!ordered && b) items.push(b[1]);
        else if (li && items.length && /^\s{2,}/.test(lines[i])) items[items.length - 1] += " " + li; // continuation
        else break;
        i++;
      }
      const Tag = ordered ? "ol" : "ul";
      push(
        <Tag key={`l${key++}`} style={{ margin: "10px 0 14px", paddingLeft: 24, color: GM.vellumDim, lineHeight: 1.85, fontSize: 15 }}>
          {items.map((it, n) => <li key={n} style={{ marginBottom: 5 }}>{inline(it, `li${key}-${n}`)}</li>)}
        </Tag>
      );
      continue;
    }

    // paragraph — consume until a blank line or a block starter
    const para = [];
    while (i < lines.length) {
      const p = lines[i].trim();
      if (!p || p.startsWith("#") || p.startsWith("|") || p.startsWith(">")
          || /^---+$/.test(p) || /^[-*]\s+/.test(p) || /^\d+\.\s+/.test(p)) break;
      para.push(p); i++;
    }
    if (para.length) {
      push(<p key={`p${key++}`} style={{ margin: "0 0 14px", color: GM.vellumDim, lineHeight: 1.85, fontSize: 15 }}>{inline(para.join(" "), `p${key}`)}</p>);
    }
  }

  return <div style={style}>{blocks}</div>;
}

// ════════════════════════════════════════════════════════════════════
// THE BOOK, CUT AT ITS OWN HEADINGS
// ════════════════════════════════════════════════════════════════════
// ⚠ THE SECTIONS ARE DERIVED, NOT LISTED. A chapter added to
// `RULEZ_V2.md` tomorrow appears in the app's contents, its page turns,
// its search index and its progress dots with NO EDIT ANYWHERE — which
// is the difference between rendering the book and copying it. A
// hand-kept list of chapter ids would have been one more thing to drift.
const slug = (s) => String(s)
  .toLowerCase()
  .replace(/^chapter\s+\d+:\s*/, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .slice(0, 40) || "section";

function cut(md) {
  const lines = String(md).split("\n");
  const out = [];
  let part = 0;
  let cur = null;
  const seen = new Map();
  const uniq = (id) => {
    const n = (seen.get(id) || 0) + 1;
    seen.set(id, n);
    return n === 1 ? id : `${id}-${n}`;
  };
  for (const line of lines) {
    const h1 = /^#\s+(.*)$/.exec(line);
    const h2 = /^##\s+(.*)$/.exec(line);
    if (h1) {
      const title = h1[1].trim();
      const m = /^PART\s+(I{1,3}|\d+)\b/i.exec(title);
      if (m) {
        // `# PART I — A SOLREND, WITNESSED` does two jobs and it took a
        // bug to notice the second. It SETS the part every chapter after
        // it belongs to — and it also OPENS a section of its own, because
        // Part I's entire body (the worked Solrend, 160 lines of it) sits
        // under this heading with no `##` of its own. Treating it as a
        // pure divider swept all of it into J'ynxi's Invitation, which
        // came out fourteen thousand characters long.
        part = { I: 1, II: 2, III: 3 }[m[1].toUpperCase()] || Number(m[1]) || part;
        cur = { id: uniq(slug(title.replace(/^PART\s+\S+\s*[—-]\s*/i, ""))), title, part, body: [] };
        out.push(cur);
        continue;
      }
      // The title page — everything above the first heading is the
      // preamble and is exported separately.
      if (!cur) continue;
    }
    if (h2) {
      const title = h2[1].trim();
      cur = { id: uniq(slug(title)), title, part, body: [] };
      out.push(cur);
      continue;
    }
    if (cur) cur.body.push(line);
  }
  return out.map((s) => ({ ...s, body: s.body.join("\n").trim() }));
}

/** Every `##` section of RULEZ_V2.md, in order, with its Part. */
export const RULEZ_SECTIONS = cut(RULEZ_V2_MD);

/** The book's own front matter — everything above the first `##`. */
export const RULEZ_PREAMBLE = String(RULEZ_V2_MD).split(/^##\s+/m)[0].trim();

/** One chapter, rendered. */
export function RulezChapter({ id }) {
  const sec = RULEZ_SECTIONS.find((s) => s.id === id);
  if (!sec) return null;
  return (
    <>
      <div style={{
        fontFamily: GM.font, fontSize: 24, fontWeight: 900, fontStyle: "italic",
        color: GM.inkGoldBright, marginBottom: 4,
      }}>{sec.title}</div>
      <div style={{ height: 1, background: `linear-gradient(90deg, ${GM.inkGold}88, transparent)`, marginBottom: 18 }} />
      <RulezMarkdown md={sec.body} />
    </>
  );
}

// ── THE ONE NORMALIZER, AND WHY IT IS EXPORTED ──────────────────────
// ⚠ FOUND LIVE 2026-09-14, by typing into the well and watching it come
// up dry on a phrase that is printed in the book.
// This transformation used to live INSIDE `rulezText`, so the index was
// normalized and the QUERY was not — `Rulebook.js` only did
// `.trim().toLowerCase()`. Every one of these strips a hyphen to a space
// in the haystack and left it a hyphen in the needle, so NO HYPHENATED
// PHRASE IN THE BOOK COULD EVER BE FOUND: "half-turn", "face-down",
// "Lyt-attack", "rock-paper-scissors", "forty-five seconds", "Pipe-Lich".
// Terror's rules are full of them, and a search well that silently
// refuses the most-used compound in the rulebook is worse than no well —
// a player concludes the rule is not written down.
// ⭐ THE LAW: the needle and the haystack are normalized by the SAME
// FUNCTION. If you change this regex, both sides move together, because
// there is only one side.
export const rulezNormalize = (s) => String(s ?? "")
  .replace(/[#*`>|_-]+/g, " ")
  .replace(/\s+/g, " ")
  .trim();

/** Plain text of a section — for the Rulebook's search index. */
export const rulezText = (id) => {
  const sec = RULEZ_SECTIONS.find((s) => s.id === id);
  if (!sec) return "";
  return rulezNormalize(`${sec.title} ${sec.body}`);
};
